import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "lucide-react";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTransferSKUMutation } from "@/data/mutations";
import { useGetOrganizationMembershipsQuery } from "@/data/queries";
import { useDialogState } from "@/hooks/use-dialog-state";
import useStore from "@/state";

type Props = {
  skuId: string;
};

const transferSKUFormSchema = z.object({
  receivingOrganizationId: z.string().nonempty(),
});

export const TransferSKUDialog: FC<Props> = ({ skuId }) => {
  const { dialog, closeDialog } = useDialogState();
  const transferSKUMutation = useTransferSKUMutation();
  const { data } = useGetOrganizationMembershipsQuery();
  const { session } = useStore();

  const form = useForm({
    resolver: zodResolver(transferSKUFormSchema),
    defaultValues: {
      receivingOrganizationId: "",
    },
  });

  const handleSubmit = (values: z.infer<typeof transferSKUFormSchema>) => {
    transferSKUMutation.mutate({
      skuId,
      workOSOrgId: values.receivingOrganizationId,
    });
  };

  if (!data) {
    return null;
  }

  return (
    <AlertDialog open={dialog?.name === "transfer-sku"}>
      <AlertDialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <AlertDialogHeader>
              <AlertDialogTitle>Transfer SKU {skuId}</AlertDialogTitle>
              <AlertDialogDescription>
                This will transfer the SKU and all of its associated tags to
                another organization that you are a part of.
              </AlertDialogDescription>

              <FormField
                control={form.control}
                name="receivingOrganizationId"
                render={({ field }) => (
                  <FormItem className="my-6">
                    <FormLabel>Receiving Organization*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an organization" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {data.map((org) => (
                          <SelectItem
                            key={org.id}
                            value={org.organization_id}
                            disabled={
                              org.organization_id === session?.organization_id
                            }
                          >
                            {org.organization_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button
                variant="outline"
                onClick={closeDialog}
                disabled={transferSKUMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !form.formState.isValid || transferSKUMutation.isPending
                }
              >
                {transferSKUMutation.isPending ? (
                  <Loader className="animate-spin" />
                ) : null}
                Transfer
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
};
